import { Divider, Group, Stack } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { Banner, Button, TextArea } from '@repo/foundations';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { BaseApprovalFormProps } from '../types';

export function ManualApprovalForm({
	request,
	onSubmit,
	onClose,
	isLoading,
	disabled,
}: BaseApprovalFormProps) {
	const formik = useFormik({
		initialValues: {
			duration: request.requested_expires_at
				? new Date(request.requested_expires_at)
				: null,
			reason: '',
		},
		validationSchema: Yup.object().shape({
			duration: Yup.date().nullable(),
			reason: Yup.string().required(
				'Approval message is required for manual grants.'
			),
		}),
		validateOnBlur: true,
		validateOnChange: true,
		onSubmit: async (values) => {
			await onSubmit(values, 'manual');
		},
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			<Stack spacing="md">
				<Stack spacing="xs">
					<Banner
						message="This is a manual access request. You must manually grant access to
						the user in the integration outside of Secoda. Provide the user with
						specific instructions in the approval message field below."
					/>
				</Stack>

				<DateTimePicker
					label="Access end date"
					placeholder="Indefinite"
					name="duration"
					minDate={dayjs().toDate()}
					value={formik.values.duration}
					onChange={(durationValue) =>
						formik.setFieldValue('duration', durationValue)
					}
					clearable
					error={formik.errors.duration}
					onBlur={formik.handleBlur}
					popoverProps={{
						withinPortal: true,
					}}
					disabled={disabled}
				/>
				<TextArea
					label="Approval Message (Required)"
					placeholder="Provide specific instructions for manual access..."
					name="reason"
					value={formik.values.reason}
					onChange={(event) =>
						formik.setFieldValue('reason', event.currentTarget.value)
					}
					error={formik.errors.reason}
					onBlur={formik.handleBlur}
					required
					minRows={3}
					disabled={disabled}
				/>
			</Stack>
			<Divider my="md" orientation="horizontal" />
			<Group spacing="xs" position="right">
				<Button onClick={onClose} disabled={isLoading || disabled}>
					Cancel
				</Button>
				<Button
					variant="primary"
					type="submit"
					disabled={isLoading || !formik.isValid || !formik.dirty || disabled}
					loading={isLoading}
				>
					{disabled
						? 'Approved'
						: isLoading
							? 'Approving...'
							: 'Approve request'}
				</Button>
			</Group>
		</form>
	);
}
